import { Field } from 'formik'
import FormikDatePicker, { formatDateValue } from './FormikDatePicker'
import PhoneField from '../../2compounds/PhoneField'
import { makeStyles } from '@material-ui/core/styles'
import { useEffect } from 'react'

const InputField = ({
  type,
  name,
  maxLength,
  required,
  label,
  value,
  editMode,
  options,
  setFieldValue,
  values,
  onChange,
  placeholder,
  helperText
}) => {
  const classes = useStyles()
  const helperTextId = name
    ? `${name.replace(/[^a-z0-9_-]/gi, '-')}-helper-text`
    : undefined

  useEffect(() => {
    if (
      type === 'select' &&
      !value &&
      options?.length &&
      setFieldValue &&
      editMode
    ) {
      setFieldValue(name, options[0].value ?? '')
    }
    // eslint-disable-next-line
  }, [])

  const componentInputModeClassName = type => ({
    ...(type === 'textarea'
      ? { className: `${classes.inputField} ${classes.textareaInput}` }
      : type === 'phone'
      ? { className: classes.phoneField }
      : { className: classes.inputField })
  })

  const getComponentDisplayModeClassName = type => ({
    ...(type === 'textarea'
      ? {
          className: `${classes.inputFieldDisplayMode} ${classes.textareaInput}`
        }
      : { className: classes.inputFieldDisplayMode })
  })

  // Component ClassName
  const getComponentClassName = (type, mode) =>
    mode === 'input'
      ? componentInputModeClassName(type)
      : getComponentDisplayModeClassName(type)

  // Component type
  const getComponentType = () => ({
    ...(['text', 'url', 'date', 'email', 'hidden'].includes(type)
      ? { type: type }
      : { as: type })
  })

  // Component display value when not in Edit mode
  const getInputValue = () => {
    if (type === 'select') {
      const oneOption = options.find(o => o.value === value)
      return oneOption ? oneOption.text : ''
    }

    if (type === 'date') return formatDateValue(value, 'toYYYY-MM-DD')

    return value
  }

  const componentProps = (type, mode) => ({
    ...getComponentClassName(type, mode),
    ...getComponentType(),
    ...(name && { name: name }),
    ...(required && { required: required }),
    ...(maxLength && { maxLength: maxLength }),
    ...(placeholder && { placeholder }),
    ...(helperTextId && helperText && { 'aria-describedby': helperTextId }),
    ...(type === 'date' && { setFieldValue: setFieldValue, value: value }),
    ...(onChange && { onChange })
  })

  const phoneFieldProps = (type, mode) => ({
    ...getComponentClassName(type, mode),
    name,
    values,
    setFieldValue,
    editMode,
    required: true
  })

  // Component Props
  const getCompponentProps = (type, mode) =>
    type === 'phone' ? phoneFieldProps(type, mode) : componentProps(type, mode)

  const getInputField = (type, inputFieldProps) => {
    const copyOfInputProps = {...inputFieldProps} //JSON.parse(JSON.stringify(inputFieldProps))
    if (type === 'url') {
      copyOfInputProps.placeholder = 'http://www.example.com'
    }
    const inputFields = {
      select: (
        <Field {...copyOfInputProps}>
          {options &&
            options.map(option => (
              <option
                key={option.key ? option.key : option.value}
                value={option.value ?? ''}
              >
                {option.text}
              </option>
            ))}
        </Field>
      ),
      date: <FormikDatePicker {...inputFieldProps} />,
      phone: <PhoneField {...inputFieldProps} />,
      default: <Field {...copyOfInputProps} />
    }

    return inputFields[type] || inputFields['default']
  }

  return (
    <div className={classes.root}>
      <div className={classes.labels}>
        {label}
        {required && <div className={classes.required}>*</div>}
      </div>
      {editMode ? (
        getInputField(type, getCompponentProps(type, 'input'))
      ) : (
        <div {...getComponentClassName(type, 'display')}>{getInputValue()}</div>
      )}
      {editMode && helperText && (
        <div id={helperTextId} className={classes.helperText}>
          {helperText}
        </div>
      )}
    </div>
  )
}

export default InputField

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: '5px 0'
  },
  labels: {
    width: '100%',
    padding: '0 5px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: '3px',
    color: theme.palette.tertiary.main,
    ...theme.typography.h3
  },
  required: {
    marginLeft: '5px',
    color: theme.palette.primary.accent,
    ...theme.typography.h3
  },
  helperText: {
    width: '100%',
    padding: '3px 5px 0',
    color: theme.palette.tertiary.main,
    fontSize: '11px',
    lineHeight: 1.3
  },
  inputField: {
    width: '100%',
    height: '25px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '1px 5px',
    borderRadius: '5px',
    border: `1px solid ${theme.palette.tertiary.accent}`,
    color: theme.palette.primary.main,
    ...theme.typography.p,
    '&:focus': {
      outline: 'none'
    }
  },
  inputFieldDisplayMode: {
    width: '100%',
    height: '25px',
    padding: '1px 5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.primary.main,
    ...theme.typography.p
  },
  /*
  CARL NOTE - this was the original. Trying to fix styling
  issues in CRUD Admin. Ideally CRUD admin should get it's
  own styling.

  inputFieldDisplayMode: {
    width: '100%',
    height: '25px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: '1px 5px',
    whiteSpace: 'pre-wrap',
    color: theme.palette.primary.main,
    ...theme.typography.p
  },
  */
  textareaInput: {
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    maxWidth: '100%',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 5
    }
  },
  phoneField: {
    width: '100%',
    '& .form-control': {
      width: '100%',
      height: '25px'
    }
  }
}))
